import { useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { useAssets } from '../data/fleet.ts'
import { pickChecklist, useAvailability, useChecklistItems, useChecklistTemplates, useChecklistVersions, type ChecklistItemRow } from '../data/safety.ts'
import { parseDecimal } from '../lib/format.ts'
import { useMerged } from '../sync/hooks.ts'
import { BackLink, Banner, DisabledHint, Field, Pill } from '../ui/components.tsx'
import { useAction, useWriteAccess } from '../ui/hooks.ts'
import { PhotoField, PhotoStrip } from '../photos/PhotoField.tsx'
import { usePhotoDraft } from '../photos/usePhotos.ts'
import { AvailabilityBanner } from './SafetyPanel.tsx'

interface Answer { response?: 'ok' | 'defect' | 'na'; numeric?: string; note?: string; escalate?: boolean }
type Outcome = 'pass' | 'pass_with_notes' | 'fail_noncritical' | 'fail_critical'

const lang = (l: string): 'fr' | 'en' => (l.toLowerCase().startsWith('en') ? 'en' : 'fr')

/** The pre-start check (GK-009). A SAFETY FACT: it works with no signal, and a critical defect shows the machine out of service on this device at
 *  once (the server decides again when it syncs). Every point must be answered; there is no "skip". */
export function CheckPage() {
  const { t, i18n } = useTranslation()
  const { assetId } = useParams({ from: '/assets/$assetId/check' })
  /** M23 item 14: opened from the start flow, the check knows its job and leads straight back to it */
  const search = useSearch({ strict: false }) as { job?: string; asset?: string }
  const { send, engine } = useClub()
  const st = useEngineState()
  const access = useWriteAccess('inspection.submit')
  const execute = useWriteAccess('job.execute')                   // cosmetic: whether the result page offers "Commencer le travail" (the server decides again)
  const assets = useAssets(); const versions = useChecklistVersions(); const allItems = useChecklistItems(); const templates = useChecklistTemplates()
  const inspections = useMerged<{ id: string; outcome: Outcome }>('inspection', 'fleet.inspection')
  const { isBlocked } = useAvailability()
  const { busy, error, run } = useAction()
  const inspectionId = useRef(newId())
  const startedAt = useRef(new Date().toISOString())
  const photos = usePhotoDraft()
  const [answers, setAnswers] = useState<Record<string, Answer>>({})
  const [sent, setSent] = useState(false)

  const asset = assets?.find((a) => a.id === assetId)
  const version = useMemo(() => (asset && versions ? pickChecklist(asset, versions) : null), [asset, versions])
  const items = useMemo(() => (version && allItems ? allItems.filter((i) => i.version_id === version.id).sort((a, b) => a.sort_order - b.sort_order) : []), [version, allItems])
  const template = templates?.find((x) => x.id === version?.template_id)
  const set = (key: string, patch: Partial<Answer>) => setAnswers((s) => ({ ...s, [key]: { ...s[key], ...patch } }))

  if (!assets || !versions || !allItems) return <p>{t('common.loading')}</p>
  if (!asset) return <section className="flex flex-col gap-3"><p>{t('assets.empty')}</p><Link to="/assets" className="btn">{t('common.back')}</Link></section>

  const complete = (i: ChecklistItemRow): boolean => {
    const a = answers[i.item_key]
    if (!a) return false
    return i.response_type === 'numeric' ? parseDecimal(a.numeric ?? '') !== null : !!a.response
  }
  const answered = items.filter(complete).length
  const anyCritical = items.some((i) => answers[i.item_key]?.response === 'defect' && (i.is_critical || answers[i.item_key]?.escalate))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!version || answered < items.length) return
    void run(async () => {
      const { commandId } = await send('inspection.submit@1', {
        inspection_id: inspectionId.current, asset_id: asset.id, checklist_version_id: version.id, started_at: startedAt.current,
        responses: items.map((i) => {
          const a = answers[i.item_key]!
          const note = a.note?.trim() || undefined
          if (i.response_type === 'numeric') return { item_key: i.item_key, response: 'ok' as const, numeric_value: parseDecimal(a.numeric ?? '')!, ...(note ? { note } : {}) }
          return { item_key: i.item_key, response: a.response!, ...(note ? { note } : {}), ...(a.response === 'defect' && !i.is_critical && a.escalate ? { escalate: true } : {}) }
        }),
      })
      await photos.commit({ entity_type: 'fleet.inspection', entity_id: inspectionId.current }, [commandId])
      setSent(true)
    })
  }

  if (sent) {
    const outcome = inspections?.find((x) => x.id === inspectionId.current)?.outcome
    const out = outcome === 'fail_critical' || isBlocked(asset.id)
    const online = st.online !== false && (typeof navigator === 'undefined' || navigator.onLine !== false)
    // M23 item 14: the check leads to the job. With the job known (opened from the start flow) the link goes straight to its start with the machine just checked; a
    // machine now out of service is never carried along: with the job, "Choisir une autre machine" opens its start without one, without it the chooser opens plain.
    const toStart = out ? (search.job ? { job: search.job } : {}) : { ...(search.job ? { job: search.job } : {}), asset: asset.id }
    return (
      <section className="flex flex-col gap-4" data-testid="check-result">
        <h1 className="text-2xl font-bold">{asset.readable_tag}</h1>
        {out
          ? <><Banner tone="error"><span className="text-lg font-bold" data-testid="result-out">{t('check.result.fail_critical')}</span></Banner><AvailabilityBanner assetId={asset.id} /></>
          : <Banner tone={outcome === 'fail_noncritical' ? 'warn' : 'ok'}><span className="text-lg font-bold" data-testid="result-ok">{t(`check.result.${outcome ?? 'saved'}`)}</span></Banner>}
        <p className="meta">{t('check.savedHelp')}</p>
        {/* M23 item 58: a critical check makes the server open a repair order at the sync; the mechanic is told, and can sync now */}
        {outcome === 'fail_critical' && (
          <div className="flex flex-col gap-2" data-testid="check-repair-order">
            <Banner tone="info">{t('check.repairOrderComing')}</Banner>
            {online && <button type="button" className="btn" disabled={st.phase === 'syncing'} onClick={() => void engine.sync({ force: true })} data-testid="check-sync-now">{st.phase === 'syncing' ? t('sync.syncing') : t('sync.now')}</button>}
          </div>
        )}
        <PhotoStrip entityId={inspectionId.current} />
        {execute.permitted && (
          <Link to="/work/start" search={toStart as never} className="btn btn-primary" data-testid="check-start-work">{out && search.job ? t('work.chooseMachineAgain') : t('check.startWork')}</Link>
        )}
        <div className="flex flex-wrap gap-3">
          <Link to="/assets/$assetId" params={{ assetId: asset.id }} className="btn">{t('check.backToAsset')}</Link>
          <Link to="/scan" className="btn">{t('check.another')}</Link>
        </div>
      </section>
    )
  }

  const l = lang(i18n.language)
  return (
    <section className="flex flex-col gap-4" data-testid="check-page">
      <BackLink to="/assets/$assetId" params={{ assetId: asset.id }}>← {t('common.back')}</BackLink>
      <div>
        <h1 className="text-2xl font-bold">{t('check.title')}</h1>
        <p className="text-lg font-semibold" data-testid="check-asset">{asset.readable_tag}</p>
        {version && <p className="meta">{t('check.version', { name: template?.name ?? '', version: version.version_no })}</p>}
      </div>
      {!access.permitted && <Banner tone="warn">{t('check.notAllowed')}</Banner>}
      {access.readOnlyMode && <Banner tone="warn">{t('platform.readOnly')}</Banner>}
      {asset.lifecycle === 'retired' && <Banner tone="info">{t('assets.retiredBanner')}</Banner>}
      {isBlocked(asset.id) && <AvailabilityBanner assetId={asset.id} />}
      {!version ? (
        <>
          <Banner tone="warn">{t('check.noChecklist')}</Banner>
          <Link to="/assets/$assetId/defect" params={{ assetId: asset.id }} className="btn">{t('defect.report')}</Link>
        </>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <ol className="flex flex-col gap-3" data-testid="check-items">
            {items.map((i) => (
              <li key={i.id} className="card flex flex-col gap-3" data-testid="check-item" data-item={i.item_key}>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-lg font-semibold">{i.label[l]}</p>
                  {i.is_critical && <Pill tone="red">{t('check.critical')}</Pill>}
                </div>
                {i.response_type === 'numeric' ? (
                  <Field label={t('check.value', { unit: i.unit_code ? t(`meters.units.${i.unit_code}`, { defaultValue: i.unit_code }) : '' })} htmlFor={`n-${i.item_key}`}>
                    <input id={`n-${i.item_key}`} className="input" inputMode="decimal" value={answers[i.item_key]?.numeric ?? ''} onChange={(e) => set(i.item_key, { numeric: e.target.value, response: 'ok' })} data-testid="check-numeric" />
                  </Field>
                ) : (
                  <div className={`grid gap-2 ${i.response_type === 'ok_defect_na' ? 'grid-cols-3' : 'grid-cols-2'}`} role="group" aria-label={i.label[l]}>
                    {(i.response_type === 'ok_defect_na' ? (['ok', 'defect', 'na'] as const) : (['ok', 'defect'] as const)).map((r) => {
                      const on = answers[i.item_key]?.response === r
                      const tone = on ? (r === 'defect' ? 'btn-danger' : 'btn-primary') : ''
                      return <button key={r} type="button" className={`btn ${tone}`} aria-pressed={on} onClick={() => set(i.item_key, { response: r })} data-testid={`answer-${r}`}>{t(`check.answer.${r}`)}</button>
                    })}
                  </div>
                )}
                {answers[i.item_key]?.response === 'defect' && (
                  <div className="flex flex-col gap-3">
                    <Field label={t('check.note')} htmlFor={`note-${i.item_key}`}>
                      <input id={`note-${i.item_key}`} className="input" maxLength={500} value={answers[i.item_key]?.note ?? ''} onChange={(e) => set(i.item_key, { note: e.target.value })} data-testid="check-note" />
                    </Field>
                    {!i.is_critical && (
                      <label className="flex min-h-12 items-start gap-3 font-medium">
                        <input type="checkbox" className="mt-1 size-6 shrink-0" checked={!!answers[i.item_key]?.escalate} onChange={(e) => set(i.item_key, { escalate: e.target.checked })} data-testid="check-escalate" />
                        <span>{t('check.escalate')}</span>
                      </label>
                    )}
                    {(i.is_critical || answers[i.item_key]?.escalate) && <Banner tone="error">{t('check.criticalWarn')}</Banner>}
                  </div>
                )}
              </li>
            ))}
          </ol>
          <div className="flex flex-col gap-1"><h2 className="text-lg font-bold">{t('photos.checkHint')}</h2><PhotoField draft={photos} disabled={!access.allowed} /></div>
          {anyCritical && <Banner tone="error">{t('safety.isolate')}</Banner>}
          <p className="text-sm font-semibold" data-testid="check-progress">{t('check.progress', { count: answered, total: items.length })}</p>
          {error && <Banner tone="error">{error}</Banner>}
          <button className="btn btn-primary" disabled={busy || answered < items.length || !access.allowed || asset.lifecycle === 'retired'} data-testid="check-submit">{busy ? t('common.saving') : t('check.submit')}</button>
          <DisabledHint show={answered < items.length && access.allowed} testId="check-hint">{t('check.incomplete')}</DisabledHint>
        </form>
      )}
    </section>
  )
}

const SEVERITIES = ['minor', 'major', 'critical'] as const

/** Report a defect found outside a checklist. A critical one takes the machine out of service on this device at once: the form asks what happened first and
 *  how bad after (M23 item 20), and a critical one a second tap, since the jardinier cannot reverse it himself. */
export function DefectPage() {
  const { t } = useTranslation()
  const { assetId } = useParams({ from: '/assets/$assetId/defect' })
  const { send } = useClub()
  const assets = useAssets()
  const access = useWriteAccess('defect.report')
  const { busy, error, run } = useAction()
  const defectId = useRef(newId())
  const photos = usePhotoDraft()
  const [severity, setSeverity] = useState<(typeof SEVERITIES)[number]>('minor')
  const [description, setDescription] = useState('')
  const [sent, setSent] = useState<(typeof SEVERITIES)[number] | null>(null)
  const [confirming, setConfirming] = useState(false)
  const asset = assets?.find((a) => a.id === assetId)
  if (!assets) return <p>{t('common.loading')}</p>
  if (!asset) return <section className="flex flex-col gap-3"><p>{t('assets.empty')}</p><Link to="/assets" className="btn">{t('common.back')}</Link></section>

  if (sent) {
    return (
      <section className="flex flex-col gap-4" data-testid="defect-sent">
        <h1 className="text-2xl font-bold">{asset.readable_tag}</h1>
        <Banner tone={sent === 'critical' ? 'error' : 'ok'}><span className="text-lg font-bold">{t(sent === 'critical' ? 'defect.sentCritical' : 'defect.sent')}</span></Banner>
        {sent === 'critical' && <AvailabilityBanner assetId={asset.id} />}
        <PhotoStrip entityId={defectId.current} />
        <Link to="/assets/$assetId" params={{ assetId: asset.id }} className="btn btn-primary">{t('check.backToAsset')}</Link>
      </section>
    )
  }
  return (
    <section className="flex flex-col gap-4" data-testid="defect-page">
      <BackLink to="/assets/$assetId" params={{ assetId: asset.id }}>← {t('common.back')}</BackLink>
      <div><h1 className="text-2xl font-bold">{t('defect.report')}</h1><p className="text-lg font-semibold">{asset.readable_tag}</p></div>
      {!access.permitted && <Banner tone="warn">{t('defect.notAllowed')}</Banner>}
      {access.readOnlyMode && <Banner tone="warn">{t('platform.readOnly')}</Banner>}
      <form className="flex flex-col gap-4" onSubmit={(e: FormEvent) => {
        e.preventDefault()
        if (severity === 'critical' && !confirming) { setConfirming(true); return }
        void run(async () => {
          const { commandId } = await send('defect.raise@1', { defect_id: defectId.current, asset_id: asset.id, severity, description: description.trim() })
          await photos.commit({ entity_type: 'fleet.defect', entity_id: defectId.current }, [commandId])
          setSent(severity)
        })
      }}>
        <Field label={t('defect.description')} htmlFor="defect-description">
          <textarea id="defect-description" className="input" rows={4} maxLength={500} required autoFocus value={description} onChange={(e) => setDescription(e.target.value)} data-testid="defect-description" />
        </Field>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold">{t('defect.severityLabel')}</legend>
          {SEVERITIES.map((s) => (
            <label key={s} className={`card flex min-h-14 items-center gap-3 font-medium ${severity === s ? (s === 'critical' ? '!border-red-600' : '!border-brand') : ''}`}>
              <input type="radio" name="severity" className="size-6" checked={severity === s} onChange={() => { setSeverity(s); setConfirming(false) }} data-testid={`severity-${s}`} />
              <span>{t(`defect.severityHelp.${s}`)}</span>
            </label>
          ))}
        </fieldset>
        {severity === 'critical' && <Banner tone="error">{t('check.criticalWarn')} {t('safety.isolate')}</Banner>}
        <div className="flex flex-col gap-1"><h2 className="text-lg font-bold">{t('photos.defectHint')}</h2><PhotoField draft={photos} disabled={!access.allowed} /></div>
        {error && <Banner tone="error">{error}</Banner>}
        {severity === 'critical' && confirming ? (
          <div className="flex flex-col gap-2" data-testid="defect-confirm-row">
            <p className="font-medium">{t('defect.confirmHelp')}</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button type="submit" className="btn btn-danger" disabled={busy || !description.trim() || !access.allowed} data-testid="defect-confirm">{busy ? t('common.saving') : t('defect.confirmCritical')}</button>
              <button type="button" className="btn" disabled={busy} onClick={() => setConfirming(false)} data-testid="defect-confirm-cancel">{t('common.cancel')}</button>
            </div>
          </div>
        ) : (
          <button className={`btn ${severity === 'critical' ? 'btn-danger' : 'btn-primary'}`} disabled={busy || !description.trim() || !access.allowed} data-testid="defect-submit">{busy ? t('common.saving') : severity === 'critical' ? t('defect.submitCritical') : t('defect.submit')}</button>
        )}
        <DisabledHint show={!description.trim() && access.allowed} testId="defect-hint">{t('defect.describeToSend')}</DisabledHint>
      </form>
    </section>
  )
}
